import { useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";

/**
 * Sticky reading-progress bar with time-remaining indicator.
 * Watches the referenced article container's scroll position within the viewport
 * and computes:
 *  - progress: 0-1 (share of the article scrolled past)
 *  - minutesLeft: rounded whole minutes based on word count (200 wpm)
 */
export const ReadingProgress = ({ targetRef, wordCount = 0 }) => {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(0);

  useEffect(() => {
    const compute = () => {
      const el = targetRef?.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewport = window.innerHeight;
      // total scrollable distance across the article container
      const total = rect.height - viewport * 0.5;
      // how far we've scrolled past the article's top edge (past the fold)
      const scrolled = -rect.top + viewport * 0.15;
      const raw = total > 0 ? scrolled / total : 0;
      const clamped = Math.min(1, Math.max(0, raw));
      setProgress(clamped);
    };
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        compute();
      });
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [targetRef]);

  const totalMinutes = Math.max(1, Math.round(wordCount / 200));
  const remaining = Math.max(0, Math.ceil((1 - progress) * totalMinutes));
  const done = progress >= 0.98;

  return (
    <div
      data-testid="reading-progress"
      className="fixed top-0 inset-x-0 z-40 pointer-events-none"
    >
      {/* progress bar */}
      <div className="h-1 w-full bg-transparent">
        <div
          data-testid="reading-progress-bar"
          className="h-full bg-brand transition-[width] duration-150 ease-out shadow-[0_0_10px_rgba(31,157,42,0.5)]"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      {/* floating time-remaining pill (desktop only, appears after 3% scroll) */}
      {progress > 0.03 && (
        <div className="hidden md:flex justify-center mt-3">
          <div
            data-testid="reading-progress-pill"
            className="pointer-events-auto inline-flex items-center gap-2 bg-ink/85 backdrop-blur text-white text-xs font-semibold rounded-full px-3.5 py-1.5 shadow-lg"
          >
            <Clock className="h-3.5 w-3.5 text-brand-light" strokeWidth={2.25} />
            {done ? (
              <span data-testid="reading-progress-done">You've finished · thanks for reading</span>
            ) : (
              <>
                <span data-testid="reading-progress-remaining">{remaining} min left</span>
                <span className="text-white/40 tracking-widest">·</span>
                <span className="text-white/60">{Math.round(progress * 100)}%</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
